import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

const FAKE_FARMS = [
  { id: "farm-001", name: "Finca Las Palmas" },
  { id: "farm-002", name: "Finca El Roble" },
  { id: "farm-003", name: "Finca La Esperanza" },
  { id: "farm-004", name: "Finca San José" },
  { id: "farm-005", name: "Finca Santa Rita" },
];

const FAKE_LOTS_BY_FARM = {
  "farm-001": [
    { id: "lot-001A", name: "Lote A" },
    { id: "lot-001B", name: "Lote B" },
    { id: "lot-001C", name: "Lote C" },
    { id: "lot-001D", name: "Lote D" },
    { id: "lot-001E", name: "Lote E" },
  ],
  "farm-002": [
    { id: "lot-002A", name: "Lote A" },
    { id: "lot-002B", name: "Lote B" },
    { id: "lot-002C", name: "Lote C" },
    { id: "lot-002D", name: "Lote D" },
    { id: "lot-002E", name: "Lote E" },
  ],
};

const FAKE_COLLABORATORS = [
  { id: "col-0001", name: "Juan Pérez" },
  { id: "col-0002", name: "María Gómez" },
  { id: "col-0003", name: "Carlos Rojas" },
  { id: "col-0004", name: "Ana Martínez" },
  { id: "col-0005", name: "Luis Díaz" },
];

const PestMonitoringForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    date: "",
    farmId: "",
    farmName: "",
    lotId: "",
    lotName: "",
    activeCropUnits: [], // array
    minUnitsToInspect: "",
    performedById: "",
    inspectionRecords: "", // JSON/text
    cropUnitId: "",
    symptomsByGrowthStage: "",
    characterizationChecks: "",
    importantNotes: "",
    photoEvidence: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });
  const [availableLots, setAvailableLots] = useState([]);

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });
    if (initialValues.farmId) {
      const lots = FAKE_LOTS_BY_FARM[initialValues.farmId] || [];
      setAvailableLots(lots);
      if (initialValues.lotId && !initialValues.lotName) {
        const foundLot = lots.find((l) => l.id === initialValues.lotId);
        if (foundLot) setForm((f) => ({ ...f, lotName: foundLot.name }));
      }
    }
    if (Array.isArray(initialValues.activeCropUnits)) {
      setForm((f) => ({
        ...f,
        minUnitsToInspect: Math.ceil(
          initialValues.activeCropUnits.length * 0.1
        ),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleFarmChange = (farmId) => {
    const farm = FAKE_FARMS.find((f) => f.id === farmId);
    const lots = FAKE_LOTS_BY_FARM[farmId] || [];
    setAvailableLots(lots);
    setForm({
      ...form,
      farmId,
      farmName: farm ? farm.name : "",
      lotId: "",
      lotName: "",
      activeCropUnits: [],
      minUnitsToInspect: "",
    });
  };

  const handleLotChange = (lotId) => {
    const lot = (availableLots || []).find((l) => l.id === lotId);
    setForm({ ...form, lotId, lotName: lot ? lot.name : "" });
  };

  const handleActiveCropUnitsChange = (value) => {
    let arr = value;
    if (typeof value === "string") {
      arr = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    setForm({
      ...form,
      activeCropUnits: arr,
      minUnitsToInspect: Math.ceil(arr.length * 0.1),
    });
  };

  const onSubmitForm = () => {
    const payload = { ...form };
    if (!payload.createdAt)
      payload.createdAt = new Date().toISOString().slice(0, 10);
    // try parse JSON fields if user entered JSON text
    try {
      if (
        typeof payload.inspectionRecords === "string" &&
        payload.inspectionRecords.trim()
      ) {
        payload.inspectionRecords = JSON.parse(payload.inspectionRecords);
      }
    } catch (e) {}
    try {
      if (
        typeof payload.symptomsByGrowthStage === "string" &&
        payload.symptomsByGrowthStage.trim()
      ) {
        payload.symptomsByGrowthStage = JSON.parse(
          payload.symptomsByGrowthStage
        );
      }
    } catch (e) {}
    try {
      if (
        typeof payload.characterizationChecks === "string" &&
        payload.characterizationChecks.trim()
      ) {
        payload.characterizationChecks = JSON.parse(
          payload.characterizationChecks
        );
      }
    } catch (e) {}
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
        <SelectField
          name="farmId"
          label={strings.fields.farmId.label}
          value={form.farmId || ""}
          onChange={(e) => handleFarmChange(e.target.value)}
        >
          <option value="">--</option>
          {FAKE_FARMS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="lotId"
          label={strings.fields.lotId.label}
          value={form.lotId || ""}
          onChange={(e) => handleLotChange(e.target.value)}
        >
          <option value="">--</option>
          {availableLots.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="performedById"
          label={strings.fields.performedById.label}
          value={form.performedById || ""}
          onChange={(e) => setForm({ ...form, performedById: e.target.value })}
        >
          <option value="">--</option>
          {FAKE_COLLABORATORS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <TextField
        name="activeCropUnits"
        label={strings.fields.activeCropUnits.label}
        placeholder="cu-001, cu-002, ..."
        value={
          Array.isArray(form.activeCropUnits)
            ? form.activeCropUnits.join(", ")
            : form.activeCropUnits || ""
        }
        onChange={(e) => handleActiveCropUnitsChange(e.target.value)}
      />
      <TextField
        name="minUnitsToInspect"
        label={strings.fields.minUnitsToInspect.label}
        value={form.minUnitsToInspect || ""}
        readOnly
      />

      <TextField
        name="cropUnitId"
        label={strings.fields.cropUnitId.label}
        placeholder="cu-001"
        value={form.cropUnitId || ""}
        onChange={(e) => setForm({ ...form, cropUnitId: e.target.value })}
      />

      <TextAreaField
        name="inspectionRecords"
        label={strings.fields.inspectionRecords.label}
        placeholder='Ej: [{"cropUnitId":"cu-001","growthStage":"flowers","checks":{"MO":2,"ANT":0},"notes":"..."}]'
        value={form.inspectionRecords || ""}
        onChange={(e) =>
          setForm({ ...form, inspectionRecords: e.target.value })
        }
      />

      <TextAreaField
        name="symptomsByGrowthStage"
        label={strings.fields.symptomsByGrowthStage.label}
        placeholder='Ej: {"buds":{"MO":2},"flowers":{"ANT":1}}'
        value={form.symptomsByGrowthStage || ""}
        onChange={(e) =>
          setForm({ ...form, symptomsByGrowthStage: e.target.value })
        }
      />

      <TextAreaField
        name="characterizationChecks"
        label={strings.fields.characterizationChecks.label}
        placeholder='Ej: {"anthracnoseOnFlowers": true, "monalonionOnBuds": false}'
        value={form.characterizationChecks || ""}
        onChange={(e) =>
          setForm({ ...form, characterizationChecks: e.target.value })
        }
      />

      <TextAreaField
        name="importantNotes"
        label={strings.fields.importantNotes.label}
        value={form.importantNotes || ""}
        onChange={(e) => setForm({ ...form, importantNotes: e.target.value })}
      />

      <TextField
        name="photoEvidence"
        label={strings.fields.photoEvidence.label}
        placeholder="foto1.jpg, foto2.jpg"
        value={
          Array.isArray(form.photoEvidence)
            ? form.photoEvidence.join(", ")
            : form.photoEvidence || ""
        }
        onChange={(e) => setForm({ ...form, photoEvidence: e.target.value })}
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      date: { label: "Fecha" },
      farmId: { label: "Finca" },
      lotId: { label: "Lote" },
      activeCropUnits: { label: "Unidades de cultivo activas" },
      minUnitsToInspect: { label: "Unidades mínimas a inspeccionar (10%)" },
      performedById: { label: "Persona que realiza" },
      inspectionRecords: { label: "Registros de inspección" },
      cropUnitId: { label: "Unidad de cultivo inspeccionada" },
      symptomsByGrowthStage: { label: "Síntomas por etapa" },
      characterizationChecks: { label: "Caracterización" },
      importantNotes: { label: "Anotaciones importantes" },
      photoEvidence: { label: "Evidencia fotográfica" },
      createdAt: { label: "Fecha de creación" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
  },
});

export default PestMonitoringForm;
